import { useMutation, useQueryClient } from "@tanstack/react-query";
import { togglePostLike } from "../services/postService";
import { useAuth } from "../context/AuthContext";

export const useLikePost = () => {
  const queryClient = useQueryClient();
  const { apiCall } = useAuth();

  return useMutation({
    mutationFn: ({ postId, isCurrentlyLiked }) => {
      const action = isCurrentlyLiked ? "unlike" : "like";
      return togglePostLike(apiCall, postId, action);
    },

    onMutate: async ({ postId, isCurrentlyLiked }) => {
      // Cancel pending requests
      await queryClient.cancelQueries({ queryKey: ["feedPosts"] });
      await queryClient.cancelQueries({ queryKey: ["explorePosts"] });
      await queryClient.cancelQueries({ queryKey: ["trendingPosts"] });
      await queryClient.cancelQueries({ queryKey: ["userPosts"] });
      await queryClient.cancelQueries({ queryKey: ["collectionPosts"] });

      // Snapshot previous values
      const previousFeeds = queryClient.getQueriesData({
        queryKey: ["feedPosts"],
      });
      const previousExplore = queryClient.getQueriesData({
        queryKey: ["explorePosts"],
      });
      const previousTrending = queryClient.getQueriesData({
        queryKey: ["trendingPosts"],
      });
      const previousUserPosts = queryClient.getQueriesData({
        queryKey: ["userPosts"],
      });
      const previousCollectionPosts = queryClient.getQueriesData({
        queryKey: ["collectionPosts"],
      });

      const updatePost = (post) => {
        if (post._id === postId) {
          const currentLikes = parseInt(post.stats?.likes) || 0;

          return {
            ...post,
            isLikedByCurrentUser: !isCurrentlyLiked, // Toggle the like state
            stats: {
              ...post.stats,
              likes: isCurrentlyLiked ? currentLikes - 1 : currentLikes + 1, // ±1
            },
          };
        }
        return post;
      };

      const updateCache = (queryKey) => {
        queryClient.setQueriesData({ queryKey }, (old) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              posts: page.posts.map(updatePost),
            })),
          };
        });
      };

      updateCache(["feedPosts"]);
      updateCache(["explorePosts"]);
      updateCache(["trendingPosts"]);
      updateCache(["userPosts"]);
      updateCache(["collectionPosts"]);

      return {
        previousFeeds,
        previousExplore,
        previousTrending,
        previousUserPosts,
        previousCollectionPosts,
      };
    },

    onSuccess: (result, { postId, isCurrentlyLiked }) => {

    },

    onError: (error, { postId, isCurrentlyLiked }, context) => {
      const restoreSnapshot = (snapshots) => {
        snapshots?.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      };

      restoreSnapshot(context?.previousFeeds);
      restoreSnapshot(context?.previousExplore);
      restoreSnapshot(context?.previousTrending);
      restoreSnapshot(context?.previousUserPosts);
      restoreSnapshot(context?.previousCollectionPosts);
    },
  });
};
